import { useState, type SyntheticEvent } from 'react';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { homePathForSession, redirectIfAuthenticated } from '@/lib/auth-guards';
import { authClient } from '@/lib/auth-client';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { PasswordField, TextField } from '@/components/ui/Field';
import { AuthLayout } from '@/features/auth/AuthLayout';

export const Route = createFileRoute('/sign-in')({
  beforeLoad: () => redirectIfAuthenticated(),
  component: SignInPage,
});

function SignInPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function onSubmit(event: SyntheticEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setPending(true);
    const { error: signInError } = await authClient.signIn.email({ email, password });
    setPending(false);
    if (signInError) {
      setError('That email and password do not match. Check them and try again.');
      return;
    }
    await navigate({ to: await homePathForSession() });
  }

  return (
    <AuthLayout
      title="Log in"
      description="Pick up where you left off."
      footer={
        <>
          New here?{' '}
          <Link to="/sign-up" className="font-bold text-primary hover:underline">
            Create an account
          </Link>
          <span className="mx-2 text-muted" aria-hidden="true">
            ·
          </span>
          <Link to="/try" className="font-bold text-primary hover:underline">
            Try a question free
          </Link>
        </>
      }
    >
      <form className="grid gap-4" onSubmit={(event) => void onSubmit(event)}>
        {error ? <Alert tone="danger" title={error} /> : null}

        <TextField
          label="Email"
          type="email"
          required
          autoComplete="email"
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
          }}
        />

        <div className="grid gap-1.5">
          <PasswordField
            label="Password"
            required
            autoComplete="current-password"
            value={password}
            onChange={(event) => {
              setPassword(event.target.value);
            }}
          />
          <Link
            to="/forgot-password"
            className="justify-self-start text-small font-bold text-primary hover:underline"
          >
            Forgot your password?
          </Link>
        </div>

        <Button type="submit" size="lg" fullWidth loading={pending}>
          {pending ? 'Logging you in' : 'Log in'}
        </Button>
      </form>
    </AuthLayout>
  );
}
